import type { TypographyProps } from '@packages/shared';
import { Dialog, Typography } from '@packages/shared';
import { type GlycerinDisplayBasePayload } from '@packages/tracking/src/types/payloads';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

export type ModalMessageProps = {
  /** The text message you want to display, which can be clicked to open an dialog. */
  message: ReactNode;
  /**
   * The title for the dialog.
   */
  dialogTitle: string;
  /**
   * The content for the dialog.
   */
  dialogContent?: ReactNode;
  /**
   * This callback function is called when the dialog is opened through a click or keydown.
   */
  onOpenDialogHandler?: () => void;
  /**
   * This callback function is called after the dialog close button has been clicked.
   */
  onCloseHandler?: () => void;
  /**
   * This is props for displayOverlay tracking. it should be given to Dialog directly.
   */
  displayTrackingProps?: GlycerinDisplayBasePayload;
} & TypographyProps;

/**
 * This component can be used to display a clickable text message. It opens an Dialog on click.
 * Additionaly you can pass all Typography props, like `sx` or `variant` to the clickable text message.
 * */
export const TextWithDialog: FC<ModalMessageProps> = ({
  message,
  dialogTitle,
  dialogContent,
  onOpenDialogHandler,
  onCloseHandler,
  displayTrackingProps,
  sx,
  ...rest
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Typography
        onClick={() => {
          setIsDialogOpen(true);
          onOpenDialogHandler?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setIsDialogOpen(true);
            onOpenDialogHandler?.();
          }
        }}
        component="span"
        role="button"
        tabIndex={0}
        sx={{ textDecoration: 'underline', cursor: 'pointer', ...sx }}
        {...rest}
      >
        {message}
      </Typography>
      <Dialog
        open={isDialogOpen}
        handleClose={() => {
          setIsDialogOpen(false);
          onCloseHandler?.();
        }}
        title={dialogTitle}
        dialogContent={dialogContent}
        displayTrackingProps={displayTrackingProps}
      />
    </>
  );
};
